import dayjs, { Dayjs } from 'dayjs'
import isNil from 'lodash/isNil'
import { DUE_DATE_WIRE_FORMAT } from '../utils'
import {
  InvoiceDeliveryItemDto,
  InvoicePaymentType,
  InvoiceTemplate,
} from '../../../api/dto/invoice-delivery.dto'

const JANUARY = 1

/** Display order everywhere: the January pair first, then the rest. */
const INVOICE_TEMPLATE_ORDER: readonly InvoiceTemplate[] = [
  InvoiceTemplate.OE_FIRST,
  InvoiceTemplate.OE_FINAL,
  InvoiceTemplate.INITIAL_OE,
  InvoiceTemplate.MONTHLY,
]

export const PAYMENT_TYPE_LABELS: Record<InvoicePaymentType, string> = {
  [InvoicePaymentType.WIRE]: 'Wire',
  [InvoicePaymentType.STRIPE]: 'Stripe',
}

/** Short names on Invoice Management. Other screens use the customer labels. */
export const INVOICE_TEMPLATE_LABELS: Record<InvoiceTemplate, string> = {
  [InvoiceTemplate.MONTHLY]: 'Monthly',
  [InvoiceTemplate.INITIAL_OE]: 'Initial Enrollment OE',
  [InvoiceTemplate.OE_FIRST]: 'OE - Initial Funding',
  [InvoiceTemplate.OE_FINAL]: 'OE - Final Funding',
}

/** Names on Invoice List, the employer portal, tooltips, and email preview headers. */
export const INVOICE_TEMPLATE_CUSTOMER_LABELS: Record<InvoiceTemplate, string> =
  {
    [InvoiceTemplate.MONTHLY]: 'Monthly',
    [InvoiceTemplate.INITIAL_OE]: 'Initial Enrollment Open Enrollment',
    [InvoiceTemplate.OE_FIRST]: 'Open Enrollment - Initial Funding',
    [InvoiceTemplate.OE_FINAL]: 'Open Enrollment - Final Funding',
  }

/** Templates whose payment type ignores the billing profile. Mirrors EPS. */
const FIXED_PAYMENT_TYPES: Partial<
  Record<InvoiceTemplate, InvoicePaymentType>
> = {
  [InvoiceTemplate.OE_FIRST]: InvoicePaymentType.WIRE,
  [InvoiceTemplate.INITIAL_OE]: InvoicePaymentType.WIRE,
}

const JANUARY_ONLY_TEMPLATES: ReadonlySet<InvoiceTemplate> = new Set([
  InvoiceTemplate.OE_FIRST,
  InvoiceTemplate.OE_FINAL,
])

/** OE1 and OE2 are January's; other months take Monthly or Initial Enrollment OE. Mirrors EPS. */
export const getAllowedInvoiceTemplates = (
  billingMonth: number
): readonly InvoiceTemplate[] =>
  billingMonth === JANUARY
    ? INVOICE_TEMPLATE_ORDER
    : INVOICE_TEMPLATE_ORDER.filter((t) => !JANUARY_ONLY_TEMPLATES.has(t))

export const getAllowedTemplatesForPeriod = (
  billingPeriodStart: string
): readonly InvoiceTemplate[] =>
  getAllowedInvoiceTemplates(dayjs(billingPeriodStart).month() + 1)

/** January defaults to OE1 (the first sheet uploaded); every other month is monthly. */
export const getDefaultInvoiceTemplate = (
  billingMonth: number
): InvoiceTemplate =>
  billingMonth === JANUARY ? InvoiceTemplate.OE_FIRST : InvoiceTemplate.MONTHLY

const SATURDAY = 6
const SUNDAY = 0

/** OE1 is wired by end of the next business day, so that is the default pick. */
export const getDefaultOe1DueDate = (today: Dayjs): string => {
  let due = today.add(1, 'day')
  while (due.day() === SATURDAY || due.day() === SUNDAY) {
    due = due.add(1, 'day')
  }
  return due.format(DUE_DATE_WIRE_FORMAT)
}

export const resolveTemplatePaymentType = (
  template: InvoiceTemplate,
  profilePaymentType: InvoicePaymentType | null | undefined
): InvoicePaymentType | null =>
  FIXED_PAYMENT_TYPES[template] ?? profilePaymentType ?? null

/** Count of rows per template, in display order, skipping templates with none. */
export const countByTemplate = (
  deliveries: Pick<InvoiceDeliveryItemDto, 'invoiceTemplate'>[]
): { template: InvoiceTemplate; count: number }[] => {
  const counts = new Map<InvoiceTemplate, number>()
  for (const delivery of deliveries) {
    counts.set(
      delivery.invoiceTemplate,
      (counts.get(delivery.invoiceTemplate) ?? 0) + 1
    )
  }
  return INVOICE_TEMPLATE_ORDER.flatMap((template) => {
    const count = counts.get(template)
    return isNil(count) ? [] : [{ template, count }]
  })
}

/**
 * Keeps the server's order but pulls each company's OE2 directly under its OE1,
 * so the pair reads as one invoice in two parts.
 */
export const orderOePairs = <
  T extends Pick<InvoiceDeliveryItemDto, 'id' | 'clientId' | 'invoiceTemplate'>,
>(
  deliveries: T[]
): T[] => {
  const oe2ByClient = new Map<string, T>()
  const oe1Clients = new Set<string>()
  for (const delivery of deliveries) {
    const { clientId, invoiceTemplate } = delivery
    if (isNil(clientId)) continue
    if (invoiceTemplate === InvoiceTemplate.OE_FIRST) oe1Clients.add(clientId)
    if (invoiceTemplate === InvoiceTemplate.OE_FINAL)
      oe2ByClient.set(clientId, delivery)
  }

  const ordered: T[] = []
  for (const delivery of deliveries) {
    const { clientId, invoiceTemplate } = delivery
    const hasPair = !isNil(clientId) && oe1Clients.has(clientId)
    if (invoiceTemplate === InvoiceTemplate.OE_FINAL && hasPair) continue
    ordered.push(delivery)
    const oe2 =
      invoiceTemplate === InvoiceTemplate.OE_FIRST && !isNil(clientId)
        ? oe2ByClient.get(clientId)
        : undefined
    if (!isNil(oe2)) ordered.push(oe2)
  }
  return ordered
}

/** The OE2 invoice paired with an OE1 one (same company and month), if uploaded. */
export const findPairedOe2 = <
  T extends Pick<
    InvoiceDeliveryItemDto,
    'clientId' | 'invoiceTemplate' | 'billingPeriodStart'
  > & { isArchived?: boolean },
>(
  deliveries: T[],
  delivery: T
): T | null => {
  if (
    delivery.invoiceTemplate !== InvoiceTemplate.OE_FIRST ||
    isNil(delivery.clientId)
  ) {
    return null
  }
  return (
    deliveries.find(
      (d) =>
        d.invoiceTemplate === InvoiceTemplate.OE_FINAL &&
        d.isArchived !== true &&
        d.clientId === delivery.clientId &&
        d.billingPeriodStart === delivery.billingPeriodStart
    ) ?? null
  )
}
